import type {
  AddUnitsHealthManualEntryRequest,
  FetchEventsPayload,
  FetchEventsResponse,
  GetEventRes,
  GetUnitsHealthManualEntryResponse,
  UpdateCalendarEventRequest,
  UpdateUnitsHealthManualEntryRequest,
} from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';

export const eventsAPI = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint POST /advancements/events
     */
    getCalendarEvents: builder.query<
      FetchEventsResponse,
      {
        payload: FetchEventsPayload;
      }
    >({
      query: arg => ({
        url: `/advancements/events`,
        method: 'POST',
        body: arg.payload,
      }),
      providesTags: (result, _error, arg) =>
        result ? [{ type: tagTypes.UnitEvents, id: arg.payload.unitId }] : [],
    }),

    /**
     * Same as getCalendarEvents but for multiple calls
     */
    getMultipleCalendarEvents: builder.query<
      FetchEventsResponse[],
      {
        payloads: FetchEventsPayload[];
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.payloads.map(payload =>
          baseQuery({
            url: `/advancements/events`,
            method: 'POST',
            body: payload,
          }),
        );

        const responses = await Promise.all(requests);

        const datasArray = responses
          .filter(item => !!item.data)
          .map(item => item.data) as FetchEventsResponse[];
        return { data: datasArray };
      },
    }),

    /**
     * @esbEndpoint GET /advancements/events/:eventId
     */
    getCalendarEvent: builder.query<GetEventRes, { eventId: number }>({
      query: arg => ({
        url: `/advancements/events/${arg.eventId}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) =>
        result ? [{ type: tagTypes.Event, id: arg.eventId }] : [],
    }),

    /**
     * @esbEndpoint PUT /advancements/events/:eventId
     */
    updateCalendarEvent: builder.mutation<
      void,
      {
        eventId: number;
        unitId?: number;
        body: UpdateCalendarEventRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/events/${arg.eventId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) {
          return [];
        }
        const tags: Array<{
          id: string | number;
          type: typeof tagTypes.UnitEvents | typeof tagTypes.Event;
        }> = [{ type: tagTypes.Event, id: arg.eventId }];

        if (arg.unitId) {
          tags.push({ type: tagTypes.UnitEvents, id: arg.unitId });
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /commissioners/v2/organizations/:organizationGuid/unitsHealth/manualEntry
     */
    getUnitsHealthManualEntry: builder.query<
      GetUnitsHealthManualEntryResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/commissioners/v2/organizations/${arg.organizationGuid}/unitsHealth/manualEntry`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) =>
        result
          ? [
              {
                type: tagTypes.UnitEventHealthMetrics,
                id: arg.organizationGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint PUT /commissioners/v2/organizations/:organizationGuid/unitsHealth/manualEntry/:unitHealthIndicatorManualEntryGuid
     */
    updateUnitsHealthManualEntry: builder.mutation<
      void,
      {
        organizationGuid: string;
        unitHealthIndicatorManualEntryGuid: string;
        body: UpdateUnitsHealthManualEntryRequest;
      }
    >({
      query: arg => ({
        url: `/commissioners/v2/organizations/${arg.organizationGuid}/unitsHealth/manualEntry/${arg.unitHealthIndicatorManualEntryGuid}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: tagTypes.UnitEventHealthMetrics, id: arg.organizationGuid },
        ];
      },
    }),

    /**
     * @esbEndpoint POST /commissioners/v2/organizations/:organizationGuid/unitsHealth/manualEntry
     */
    addUnitsHealthManualEntry: builder.mutation<
      void,
      {
        organizationGuid: string;
        body: AddUnitsHealthManualEntryRequest;
      }
    >({
      query: arg => ({
        url: `/commissioners/v2/organizations/${arg.organizationGuid}/unitsHealth/manualEntry`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: tagTypes.UnitEventHealthMetrics, id: arg.organizationGuid },
        ];
      },
    }),
  }),
});

export const {
  useGetCalendarEventsQuery,
  useGetCalendarEventQuery,
  useLazyGetCalendarEventsQuery,
  useGetMultipleCalendarEventsQuery,
  useUpdateCalendarEventMutation,
  useGetUnitsHealthManualEntryQuery,
  useUpdateUnitsHealthManualEntryMutation,
  useAddUnitsHealthManualEntryMutation,
} = eventsAPI;
